/**
 * Consolidated Statement PDF Template
 *
 * Generates a multi-page PDF showing all invoices and payments across all branches
 * for enterprise customers. This is the single multi-page format.
 *
 * Phase 3C - Consolidated Statement PDF Template
 * User Story: US-005 (Invoice Generation Enhancement)
 */

import React from 'react'
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer'
import { getFarmLogoUrl } from '../../supabase-client'
import { parseDateString } from '../../utils/dateUtils'

// Define styles for the consolidated statement PDF
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Times-Roman',
    fontSize: 10,
    paddingTop: 35,
    paddingLeft: 35,
    paddingRight: 35,
    paddingBottom: 65,
    lineHeight: 1.5,
  },

  // Header Section
  header: {
    flexDirection: 'row',
    marginBottom: 20,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },

  headerLeft: {
    flex: 1,
  },

  headerRight: {
    flex: 1,
    alignItems: 'flex-end',
  },

  logo: {
    maxWidth: 100,
    maxHeight: 80,
    marginBottom: 10,
    objectFit: 'contain',
  },

  companyName: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 5,
  },

  companyInfo: {
    fontSize: 9,
    lineHeight: 1.3,
    marginBottom: 2,
  },

  statementTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'right',
  },

  statementDetails: {
    fontSize: 10,
    textAlign: 'right',
    lineHeight: 1.4,
  },

  // Customer Section
  customerSection: {
    marginBottom: 20,
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    textDecoration: 'underline',
  },

  customerText: {
    fontSize: 10,
    lineHeight: 1.4,
    marginBottom: 2,
  },

  periodText: {
    fontSize: 11,
    marginTop: 5,
    fontStyle: 'italic',
  },

  // Grand Totals Section
  grandTotalsSection: {
    marginBottom: 25,
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: '#000000',
  },

  grandTotalsTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    backgroundColor: '#e8e8e8',
    padding: 10,
    borderBottomWidth: 2,
    borderBottomColor: '#000000',
  },

  grandTotalsContent: {
    padding: 12,
  },

  grandTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    fontSize: 11,
  },

  grandTotalLabel: {
    fontWeight: 'bold',
  },

  grandTotalValue: {
    fontWeight: 'bold',
    textAlign: 'right',
    minWidth: 80,
  },

  grandTotalHighlight: {
    fontSize: 13,
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 2,
    borderTopColor: '#000000',
  },

  // Branch Section
  branchSection: {
    marginBottom: 25,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
    pageBreakInside: 'avoid',
  },

  branchHeader: {
    fontSize: 13,
    fontWeight: 'bold',
    backgroundColor: '#f0f0f0',
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },

  branchContent: {
    padding: 10,
  },

  costCenterCode: {
    fontSize: 10,
    fontStyle: 'italic',
    color: '#666666',
    marginTop: 2,
  },

  // Invoices Table
  invoicesTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 8,
    marginTop: 5,
  },

  invoicesTable: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#cccccc',
    marginBottom: 15,
  },

  invoiceTableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f8f8f8',
    borderBottomWidth: 1,
    borderBottomColor: '#cccccc',
    padding: 5,
  },

  invoiceHeaderText: {
    fontSize: 9,
    fontWeight: 'bold',
  },

  invoiceRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    padding: 5,
  },

  invoiceCell: {
    fontSize: 9,
  },

  // Column widths for invoice table
  invoiceDateCol: { width: '20%' },
  invoiceNoCol: { width: '25%' },
  invoiceAmountCol: { width: '25%', textAlign: 'right' },
  invoiceStatusCol: { width: '30%', textAlign: 'right' },

  // Payments Table
  paymentsTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 8,
  },

  paymentsTable: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#cccccc',
    marginBottom: 15,
  },

  paymentTableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f8f8f8',
    borderBottomWidth: 1,
    borderBottomColor: '#cccccc',
    padding: 5,
  },

  paymentHeaderText: {
    fontSize: 9,
    fontWeight: 'bold',
  },

  paymentRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    padding: 5,
  },

  paymentCell: {
    fontSize: 9,
  },

  // Column widths for payment table
  paymentDateCol: { width: '25%' },
  paymentMethodCol: { width: '30%' },
  paymentAmountCol: { width: '45%', textAlign: 'right' },

  // Branch Summary
  branchSummary: {
    borderTopWidth: 1,
    borderTopColor: '#000000',
    paddingTop: 8,
    marginTop: 5,
  },

  branchSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
    fontSize: 10,
  },

  branchSummaryLabel: {
    fontWeight: 'bold',
  },

  branchSummaryValue: {
    textAlign: 'right',
    minWidth: 80,
  },

  noDataText: {
    fontSize: 9,
    fontStyle: 'italic',
    color: '#666666',
    padding: 8,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 35,
    right: 35,
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#000000',
    paddingTop: 10,
  },

  footerText: {
    fontSize: 9,
    color: '#666666',
    marginBottom: 3,
  },

  pageNumber: {
    fontSize: 9,
    color: '#666666',
  },
})

const ConsolidatedStatementTemplate = ({ statementData, farmInfo }) => {
  // Get logo URL from file path
  const logoUrl = farmInfo?.logo_file_path ? getFarmLogoUrl(farmInfo.logo_file_path) : null

  // Format currency
  const formatCurrency = (amount) => `£${parseFloat(amount).toFixed(2)}`

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = parseDateString(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {logoUrl && (
              <View style={{ marginBottom: 10 }}>
                <Image style={styles.logo} src={logoUrl} />
              </View>
            )}
            <Text style={styles.companyName}>{farmInfo.business_name}</Text>
            {farmInfo.address_line_1 && (
              <Text style={styles.companyInfo}>{farmInfo.address_line_1}</Text>
            )}
            {farmInfo.address_line_2 && (
              <Text style={styles.companyInfo}>{farmInfo.address_line_2}</Text>
            )}
            {farmInfo.city && (
              <Text style={styles.companyInfo}>
                {farmInfo.city}{farmInfo.postcode && `, ${farmInfo.postcode}`}
              </Text>
            )}
            {farmInfo.phone && (
              <Text style={styles.companyInfo}>Tel: {farmInfo.phone}</Text>
            )}
            {farmInfo.email && (
              <Text style={styles.companyInfo}>Email: {farmInfo.email}</Text>
            )}
          </View>

          <View style={styles.headerRight}>
            <Text style={styles.statementTitle}>CONSOLIDATED STATEMENT</Text>
            <Text style={styles.statementDetails}>
              Generated: {formatDate(statementData.generatedAt)}
            </Text>
            <Text style={styles.statementDetails}>
              Period: {formatDate(statementData.startDate)} - {formatDate(statementData.endDate)}
            </Text>
          </View>
        </View>

        {/* Customer Information */}
        <View style={styles.customerSection}>
          <Text style={styles.sectionTitle}>Customer Information</Text>
          <Text style={styles.customerText}>
            {statementData.customerInfo.name}
          </Text>
          {statementData.customerInfo.email && (
            <Text style={styles.customerText}>
              {statementData.customerInfo.email}
            </Text>
          )}
          <Text style={styles.customerText}>
            Type: {statementData.customerInfo.type}
          </Text>
          <Text style={styles.periodText}>
            Statement Period: {formatDate(statementData.startDate)} to {formatDate(statementData.endDate)}
          </Text>
        </View>

        {/* Grand Totals Summary */}
        <View style={styles.grandTotalsSection}>
          <Text style={styles.grandTotalsTitle}>Overall Summary</Text>
          <View style={styles.grandTotalsContent}>
            <View style={styles.grandTotalRow}>
              <Text style={styles.grandTotalLabel}>Total Invoiced:</Text>
              <Text style={styles.grandTotalValue}>
                {formatCurrency(statementData.totals.totalInvoiced)}
              </Text>
            </View>
            <View style={styles.grandTotalRow}>
              <Text style={styles.grandTotalLabel}>Total Paid:</Text>
              <Text style={styles.grandTotalValue}>
                {formatCurrency(statementData.totals.totalPaid)}
              </Text>
            </View>
            <View style={styles.grandTotalRow}>
              <Text style={styles.grandTotalLabel}>Outstanding Invoices:</Text>
              <Text style={styles.grandTotalValue}>
                {formatCurrency(statementData.totals.totalOutstanding)}
              </Text>
            </View>
            <View style={[styles.grandTotalRow, styles.grandTotalHighlight]}>
              <Text style={styles.grandTotalLabel}>Current Balance:</Text>
              <Text style={styles.grandTotalValue}>
                {formatCurrency(statementData.totals.currentBalance)}
              </Text>
            </View>
          </View>
        </View>

        {/* Branch Details */}
        {Object.entries(statementData.branchGroups).map(([branchId, branch], index) => {
          // Calculate branch totals
          const branchInvoiced = branch.invoices.reduce((sum, inv) => sum + inv.total, 0)
          const branchPaid = branch.payments.reduce((sum, pay) => sum + pay.amount, 0)
          const branchOutstanding = branch.invoices
            .filter(inv => !inv.paid)
            .reduce((sum, inv) => sum + inv.total, 0)

          return (
            <View key={branchId} style={styles.branchSection} wrap={false}>
              <View style={styles.branchHeader}>
                <Text>{branch.branchName}</Text>
                {branch.costCenterCode && (
                  <Text style={styles.costCenterCode}>
                    Cost Center: {branch.costCenterCode}
                  </Text>
                )}
              </View>

              <View style={styles.branchContent}>
                {/* Invoices */}
                <Text style={styles.invoicesTitle}>Invoices</Text>
                {branch.invoices && branch.invoices.length > 0 ? (
                  <View style={styles.invoicesTable}>
                    <View style={styles.invoiceTableHeader}>
                      <Text style={[styles.invoiceHeaderText, styles.invoiceDateCol]}>
                        Date
                      </Text>
                      <Text style={[styles.invoiceHeaderText, styles.invoiceNoCol]}>
                        Invoice No.
                      </Text>
                      <Text style={[styles.invoiceHeaderText, styles.invoiceAmountCol]}>
                        Amount
                      </Text>
                      <Text style={[styles.invoiceHeaderText, styles.invoiceStatusCol]}>
                        Status
                      </Text>
                    </View>
                    {branch.invoices.map((invoice, invIndex) => (
                      <View key={invoice.id || invIndex} style={styles.invoiceRow}>
                        <Text style={[styles.invoiceCell, styles.invoiceDateCol]}>
                          {formatDate(invoice.date)}
                        </Text>
                        <Text style={[styles.invoiceCell, styles.invoiceNoCol]}>
                          {invoice.invoiceNo}
                        </Text>
                        <Text style={[styles.invoiceCell, styles.invoiceAmountCol]}>
                          {formatCurrency(invoice.total)}
                        </Text>
                        <Text style={[styles.invoiceCell, styles.invoiceStatusCol]}>
                          {invoice.paid ? 'Paid' : 'Outstanding'}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : (
                  <Text style={styles.noDataText}>No invoices in this period</Text>
                )}

                {/* Payments */}
                <Text style={styles.paymentsTitle}>Payments</Text>
                {branch.payments && branch.payments.length > 0 ? (
                  <View style={styles.paymentsTable}>
                    <View style={styles.paymentTableHeader}>
                      <Text style={[styles.paymentHeaderText, styles.paymentDateCol]}>
                        Date
                      </Text>
                      <Text style={[styles.paymentHeaderText, styles.paymentMethodCol]}>
                        Method
                      </Text>
                      <Text style={[styles.paymentHeaderText, styles.paymentAmountCol]}>
                        Amount
                      </Text>
                    </View>
                    {branch.payments.map((payment, payIndex) => (
                      <View key={payment.id || payIndex} style={styles.paymentRow}>
                        <Text style={[styles.paymentCell, styles.paymentDateCol]}>
                          {formatDate(payment.date)}
                        </Text>
                        <Text style={[styles.paymentCell, styles.paymentMethodCol]}>
                          {payment.method || 'N/A'}
                        </Text>
                        <Text style={[styles.paymentCell, styles.paymentAmountCol]}>
                          {formatCurrency(payment.amount)}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : (
                  <Text style={styles.noDataText}>No payments in this period</Text>
                )}

                {/* Branch Summary */}
                <View style={styles.branchSummary}>
                  <View style={styles.branchSummaryRow}>
                    <Text style={styles.branchSummaryLabel}>Branch Total Invoiced:</Text>
                    <Text style={styles.branchSummaryValue}>
                      {formatCurrency(branchInvoiced)}
                    </Text>
                  </View>
                  <View style={styles.branchSummaryRow}>
                    <Text style={styles.branchSummaryLabel}>Branch Total Paid:</Text>
                    <Text style={styles.branchSummaryValue}>
                      {formatCurrency(branchPaid)}
                    </Text>
                  </View>
                  <View style={styles.branchSummaryRow}>
                    <Text style={styles.branchSummaryLabel}>Branch Outstanding:</Text>
                    <Text style={styles.branchSummaryValue}>
                      {formatCurrency(branchOutstanding)}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          )
        })}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            This is a consolidated statement for all branches
          </Text>
          <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          } />
        </View>
      </Page>
    </Document>
  )
}

export default ConsolidatedStatementTemplate
